import { useState, useId } from 'react';
import { Info, Loader2, AlertCircle, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { BRAND_ASSETS } from '../../config';
import {
  HiveFundProps,
  FundMetrics,
  FundraisingProgress,
  PerformanceDataPoint,
  AllocationItem,
  FundHighlight,
} from './HiveFund.types';

// ─────────────────────────────────────────────────────────────────────────────
// Utility Functions
// ─────────────────────────────────────────────────────────────────────────────

function formatCurrencyCompact(amount: number): string {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default allocation colors (used when color not specified)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_ALLOCATION_COLORS = [
  'hsl(var(--primary))',
  '#1944FF',
  '#242424',
  '#888888',
];

// ─────────────────────────────────────────────────────────────────────────────
// Default Fallback Data (used when no props provided)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_DESCRIPTION = `The Hive Fund provides diversified exposure to institutional-grade commercial and residential real estate across major U.S. metro markets. Professionally managed with quarterly distributions and transparent reporting.`;

const DEFAULT_METRICS: FundMetrics = {
  targetApy: 0.12,
  minInvestment: 25000,
  termYears: 5,
  investorCount: 847,
};

const DEFAULT_FUNDRAISING: FundraisingProgress = {
  raised: 2500000,
  target: 10000000,
  percentFunded: 25,
};

const DEFAULT_PERFORMANCE: PerformanceDataPoint[] = [
  { month: 'Jan', value: 10000 },
  { month: 'Feb', value: 10450 },
  { month: 'Mar', value: 10820 },
  { month: 'Apr', value: 11150 },
  { month: 'May', value: 11580 },
  { month: 'Jun', value: 12000 },
];

const DEFAULT_ASSET_ALLOCATION: AllocationItem[] = [
  { label: 'Multifamily Residential', percentage: 40, color: 'hsl(var(--primary))' },
  { label: 'Commercial Office', percentage: 30, color: '#1944FF' },
  { label: 'Retail', percentage: 20, color: '#242424' },
  { label: 'Industrial', percentage: 10, color: '#888888' },
];

const DEFAULT_GEO_DISTRIBUTION: AllocationItem[] = [
  { label: 'West Coast', percentage: 35 },
  { label: 'East Coast', percentage: 30 },
  { label: 'Sun Belt', percentage: 25 },
  { label: 'Midwest', percentage: 10 },
];

const DEFAULT_HIGHLIGHTS: FundHighlight[] = [
  { text: '15+ institutional-grade properties' },
  { text: 'Professional property management' },
  { text: 'Quarterly cash distributions' },
  { text: 'Transparent investor reporting' },
];

const DEFAULT_DISCLAIMER = `The Hive Fund is a private investment vehicle with a 5-year lock-up period. Minimum investment of $25,000 required. This investment involves risk, including possible loss of principal. Past performance does not guarantee future results. Please review the full prospectus before investing.`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function HiveFund({
  fundName = 'Hive Fund',
  description = DEFAULT_DESCRIPTION,
  iconUrl,
  metrics = DEFAULT_METRICS,
  fundraising = DEFAULT_FUNDRAISING,
  performanceData = DEFAULT_PERFORMANCE,
  chartTitle = 'Projected Growth (10K Investment)',
  assetAllocation = DEFAULT_ASSET_ALLOCATION,
  geographicDistribution = DEFAULT_GEO_DISTRIBUTION,
  highlights = DEFAULT_HIGHLIGHTS,
  disclaimer = DEFAULT_DISCLAIMER,
  onInvest,
  investButtonText = 'Invest in Hive Fund',
  isLoading = false,
  error = null,
}: HiveFundProps) {
  const navigate = useNavigate();
  const gradientId = useId();
  const [imageError, setImageError] = useState(false);

  const handleInvest = () => {
    if (onInvest) {
      onInvest();
    } else {
      navigate('/hive-fund');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <AlertCircle className="text-destructive" size={48} />
        <p className="text-muted-foreground text-center">{error}</p>
      </div>
    );
  }

  const iconSrc = iconUrl || BRAND_ASSETS.icon.favicon;

  return (
    <div className="space-y-6">
      {/* Main Fund Overview Card */}
      <Card className="p-6 lg:p-8 border-border">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Fund Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Fund Icon & Description */}
            <div className="flex items-start gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                role="img"
                aria-label={`${fundName} logo`}
              >
                {!imageError && iconSrc ? (
                  <img
                    src={iconSrc}
                    alt=""
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                    <Home className="w-7 h-7 text-primary" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-display font-semibold text-[#242424] mb-2">About This Fund</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </div>
            </div>

            {/* Key Metrics Grid */}
            <MetricsGrid metrics={metrics} />

            {/* Performance Chart */}
            {performanceData.length > 0 && (
              <PerformanceChart
                data={performanceData}
                title={chartTitle}
                gradientId={gradientId}
              />
            )}
          </div>

          {/* Right: Fundraising Progress */}
          <div className="space-y-6">
            <FundraisingSection fundraising={fundraising} />

            {highlights.length > 0 && (
              <HighlightsList highlights={highlights} />
            )}

            <div className="pt-6 border-t border-border space-y-3">
              <Button className="w-full" onClick={handleInvest}>
                {investButtonText}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Portfolio Composition Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {assetAllocation.length > 0 && (
          <AllocationCard
            title="Asset Allocation"
            items={assetAllocation}
            showColorDot
          />
        )}

        {geographicDistribution.length > 0 && (
          <AllocationCard
            title="Geographic Distribution"
            items={geographicDistribution}
            showColorDot={false}
          />
        )}
      </div>

      {/* Info Banner */}
      {disclaimer && (
        <Card className="p-4 border-border bg-primary/5">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-[#242424] mb-1">Important Information</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {disclaimer}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface MetricsGridProps {
  metrics: FundMetrics;
}

function MetricsGrid({ metrics }: MetricsGridProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="p-4 rounded-lg bg-[#F0F0F0] border border-border">
        <p className="text-xs text-muted-foreground mb-2">Target APY</p>
        <p className="text-2xl font-semibold text-[#242424]">{formatPercent(metrics.targetApy)}</p>
      </div>

      <div className="p-4 rounded-lg bg-[#F0F0F0] border border-border">
        <p className="text-xs text-muted-foreground mb-2">Min. Investment</p>
        <p className="text-2xl font-semibold text-[#242424]">{formatCurrencyCompact(metrics.minInvestment)}</p>
      </div>

      <div className="p-4 rounded-lg bg-[#F0F0F0] border border-border">
        <p className="text-xs text-muted-foreground mb-2">Term Length</p>
        <p className="text-2xl font-semibold text-[#242424]">{metrics.termYears} Years</p>
      </div>

      <div className="p-4 rounded-lg bg-[#F0F0F0] border border-border">
        <p className="text-xs text-muted-foreground mb-2">Investors</p>
        <p className="text-2xl font-semibold text-[#242424]">{metrics.investorCount.toLocaleString()}</p>
      </div>
    </div>
  );
}

interface PerformanceChartProps {
  data: PerformanceDataPoint[];
  title: string;
  gradientId: string;
}

function PerformanceChart({ data, title, gradientId }: PerformanceChartProps) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-[#242424] mb-3">{title}</h4>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#242424" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#242424" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="month"
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '14px',
                color: 'hsl(var(--foreground))',
              }}
              formatter={(value: number) => [`$${value.toLocaleString()}`, 'Projected Value']}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#242424"
              strokeWidth={2}
              fillOpacity={1}
              fill={`url(#${gradientId})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface FundraisingSectionProps {
  fundraising: FundraisingProgress;
}

function FundraisingSection({ fundraising }: FundraisingSectionProps) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-[#242424] mb-4">Fundraising Progress</h4>

      <div className="space-y-4">
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-3xl font-display font-semibold text-[#242424]">
              {formatCurrencyCompact(fundraising.raised)}
            </span>
            <span className="text-sm text-muted-foreground">raised</span>
          </div>
          <p className="text-sm text-muted-foreground">of {formatCurrencyCompact(fundraising.target)} target</p>
        </div>

        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-[#242424]">{fundraising.percentFunded}%</span>
          </div>
          <div
            className="relative h-2 bg-[#F0F0F0] rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={fundraising.percentFunded}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="absolute top-0 left-0 h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, fundraising.percentFunded)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface HighlightsListProps {
  highlights: FundHighlight[];
}

function HighlightsList({ highlights }: HighlightsListProps) {
  return (
    <div className="pt-6 border-t border-border space-y-3">
      {highlights.map((highlight, index) => (
        <div key={index} className="flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
          <p className="text-sm text-muted-foreground">{highlight.text}</p>
        </div>
      ))}
    </div>
  );
}

interface AllocationCardProps {
  title: string;
  items: AllocationItem[];
  showColorDot: boolean;
}

function AllocationCard({ title, items, showColorDot }: AllocationCardProps) {
  return (
    <Card className="p-6 border-border">
      <h3 className="text-base font-display font-semibold text-[#242424] mb-4">{title}</h3>

      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.label} className="flex items-center justify-between p-3 rounded-lg bg-[#F0F0F0]">
            <div className="flex items-center gap-3">
              {showColorDot && (
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color || DEFAULT_ALLOCATION_COLORS[index % DEFAULT_ALLOCATION_COLORS.length] }}
                />
              )}
              <span className="text-sm text-[#242424]">{item.label}</span>
            </div>
            <span className="text-sm font-semibold text-[#242424]">{item.percentage}%</span>
          </div>
        ))}
      </div>
    </Card>
  );
}
